import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import DriveIcon from '@mui/icons-material/CloudQueue';
import DeviceIcon from '@mui/icons-material/PhoneAndroid';
import MergeIcon from '@mui/icons-material/MergeType';
import { useAuthStore } from '../../stores/authStore';
import { syncService, ConflictResolution } from '../../services/syncService';
import { useWarbandStore } from '../../stores/warbandStore';
import { getAllWarbands } from '../../services/localStorageService';

export const ConflictResolutionModal: React.FC = () => {
  const { accessToken, pendingConflict, setSyncStatus, setPendingConflict } =
    useAuthStore();
  const setWarbands = useWarbandStore((s) => s.setWarbands);
  const [isResolving, setIsResolving] = useState(false);

  const handleResolve = async (resolution: ConflictResolution) => {
    if (!accessToken || !pendingConflict) return;

    setIsResolving(true);
    setSyncStatus('syncing');

    try {
      await syncService.applyResolution(resolution, accessToken);
      // Reload the warband list from localStorage (may have changed)
      setWarbands(getAllWarbands());
      setSyncStatus('success');
    } catch (err) {
      setSyncStatus('error', err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setIsResolving(false);
      setPendingConflict(null);
    }
  };

  if (!pendingConflict) return null;

  const { driveOnly, localOnly } = pendingConflict;

  return (
    <Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown={isResolving}>
      <DialogTitle>Google Drive — Sync Conflict</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Warbands on Google Drive and on this device don't fully match. Choose
          how to resolve the difference.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {driveOnly.length > 0 && (
            <Stack flex={1} spacing={1}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <DriveIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2">
                  On Drive only ({driveOnly.length})
                </Typography>
              </Stack>
              <List dense disablePadding>
                {driveOnly.map((w) => (
                  <ListItem key={w.id} disablePadding sx={{ pl: 1 }}>
                    <ListItemText
                      primary={w.name}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Stack>
          )}

          {driveOnly.length > 0 && localOnly.length > 0 && (
            <Divider orientation="vertical" flexItem />
          )}

          {localOnly.length > 0 && (
            <Stack flex={1} spacing={1}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <DeviceIcon fontSize="small" color="secondary" />
                <Typography variant="subtitle2">
                  On this device only ({localOnly.length})
                </Typography>
              </Stack>
              <List dense disablePadding>
                {localOnly.map((w) => (
                  <ListItem key={w.id} disablePadding sx={{ pl: 1 }}>
                    <ListItemText
                      primary={w.name}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, p: 2 }}>
        {isResolving && <CircularProgress size={20} sx={{ mr: 'auto' }} />}

        <Button
          variant="outlined"
          startIcon={<DeviceIcon />}
          onClick={() => void handleResolve('keep-local')}
          disabled={isResolving}
        >
          Keep this device
        </Button>

        <Button
          variant="outlined"
          startIcon={<DriveIcon />}
          onClick={() => void handleResolve('use-drive')}
          disabled={isResolving}
        >
          Use Drive
        </Button>

        <Button
          variant="contained"
          startIcon={<MergeIcon />}
          onClick={() => void handleResolve('merge')}
          disabled={isResolving}
        >
          Merge both
        </Button>
      </DialogActions>
    </Dialog>
  );
};
